import { List, ShowButton, useTable } from '@refinedev/antd';
import { BaseRecord } from '@refinedev/core';
import { Flex } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import FailReportStatusTag from '@/components/failReport/FailReportStatusTag';
import AppTable from '@/components/general/AppTable';
import LinkTextField from '@/components/general/LinkTextField';
import ProtectedCreateButton from '@/components/protected/ProtectedCreateButton';
import ProtectedEditButton from '@/components/protected/ProtectedEditButton';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUserContext } from '@/contexts/UserContext';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';
import { FAIL_REPORT_STATUS, FailReport } from '@/model/FailReport/FailReport';
import { RESOURCES } from '@/Resources';

import FailReportFilter from './FailReportFilter/FailReportFilter';

const FailReportsList = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const { pageSizePreference } = useUserContext();
  const [isReady, setIsReady] = useState(false);
  const {
    tableProps,
    filters,
    setFilters,
    sorters,
    setSorters,
    setCurrentPage: setCurrent,
    setPageSize,
    currentPage,
    pageSize,
  } = useTable<FailReport>({
    syncWithLocation: true,
    queryOptions: {
      refetchInterval: 300000, // 5 minutes
      enabled: isReady,
    },
    pagination: {
      pageSize: pageSizePreference,
    },
  });

  // Persistencia de filtros y paginado del listado.
  useListStatePersistence({
    resource: RESOURCES.FAIL_REPORTS,
    filters,
    setFilters,
    sorters,
    setSorters,
    currentPage,
    setCurrentPage: setCurrent,
    pageSize,
    setPageSize,
    onReady: () => setIsReady(true),
  });

  return (
    <List
      headerButtons={({ createButtonProps }) => (
        <>
          <FailReportFilter filters={filters} setFilters={setFilters} setCurrent={setCurrent} />
          <ProtectedCreateButton {...createButtonProps} />
        </>
      )}>
      <AppTable {...tableProps} rowKey="id" scroll={{ x: 1000, y: 'calc(100dvh - 310px)' }}>
        <AppTable.Column
          dataIndex="id"
          title={'Id'}
          width={50}
          render={(value, record) =>
            hasPermission('show') ? (
              <LinkTextField
                label={value}
                to={
                  record.status == FAIL_REPORT_STATUS.INSTANCE_1 ||
                  record.status == FAIL_REPORT_STATUS.INSTANCE_2
                    ? `/${RESOURCES.FAIL_REPORTS}/edit/${value}`
                    : `/${RESOURCES.FAIL_REPORTS}/show/${value}`
                }
                local
                withoutColor
              />
            ) : (
              value
            )
          }
        />
        <AppTable.Column
          width={250}
          dataIndex="category"
          title={t('Categoría')}
          render={v => v?.title ?? t('No categorizado')}
        />

        <AppTable.Column
          dataIndex="ticket"
          title={t('Ticket')}
          width={80}
          render={v => (
            <LinkTextField to={`/${RESOURCES.TICKETS}/show/${v}`} label={v} local withoutColor />
          )}
        />
        <AppTable.Column
          dataIndex="created_by"
          title={t('Creador')}
          width={150}
          render={v => v.first_name + ' ' + v.last_name}
        />
        <AppTable.Column
          dataIndex={'review_by'}
          title={t('Revisado por')}
          width={180}
          render={v => (v ? v.first_name + ' ' + v.last_name : '-')}
        />
        <AppTable.Column
          width={100}
          fixed="right"
          dataIndex={'status'}
          title={t('Estado')}
          render={(_, record) => (
            <Flex vertical align="center" justify="center" gap={4}>
              {<FailReportStatusTag failReportStatus={record.status} />}
            </Flex>
          )}
        />
        {(hasPermission('edit') || hasPermission('show')) && (
          <AppTable.Column
            dataIndex="actions"
            width={70}
            fixed="right"
            render={(_, record: BaseRecord) => (
              <Flex align="center" justify="center" gap={4}>
                {record.status == FAIL_REPORT_STATUS.INSTANCE_1 ||
                record.status == FAIL_REPORT_STATUS.INSTANCE_2 ? (
                  <ProtectedEditButton hideText size="small" recordItemId={record.id} />
                ) : (
                  <ShowButton hideText size="small" recordItemId={record.id} />
                )}
              </Flex>
            )}
          />
        )}
      </AppTable>
    </List>
  );
};

export default FailReportsList;
